import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  message, 
  Tag, 
  Space,
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Popconfirm,
  Tooltip,
  DatePicker,
  Dropdown,
  Menu,
  Badge
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  DownloadOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  DollarOutlined,
  ReloadOutlined,
  MailOutlined,
  ClockCircleOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { agencyService, Agency, AgencyFilter } from '../../services/agencyService';
import { getMergedCountryOptions } from '../../constants/countries';
import TableFilter, { FilterConfig, FilterValues } from '../../components/TableFilter/TableFilter';
import { applyFilters } from '../../utils/filterHelpers';
import { usePageSearch } from '../../hooks/usePageSearch';
import AdminResetPasswordModal from '../../components/admin/AdminResetPasswordModal';
import moment from 'moment';
import AgencyFilters from 'components/AgencyFilters/AgencyFilters';
import { text } from 'stream/consumers';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface AgencyForm {
  agencyName: string;
  licenceNumber: string;
  agencyEmail: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  contactPerson: string;
}

const AgenciesList: React.FC = () => {
  const { t } = useTranslation();
  
  // Supprimer l'erreur ResizeObserver pour éviter les avertissements dans la console
  useEffect(() => {
    const resizeObserverErrorHandler = (e: any) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    
    window.addEventListener('error', resizeObserverErrorHandler);
    
    return () => {
      window.removeEventListener('error', resizeObserverErrorHandler);
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const [agencies, setAgencies] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 0,
    pageSize: 10,
    total: 0
  });
  //const [filters, setFilters] = useState<AgencyFilter>({});
  const [filters, setFilters] = useState<{
    name?: string;
    email?: string;
    status?: string;
  }>({});
  const [tableFilterValues, setTableFilterValues] = useState<FilterValues>({});
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentAgency, setCurrentAgency] = useState<any | null>(null);
  const [form] = Form.useForm<AgencyForm>();
  const [addFundsModalVisible, setAddFundsModalVisible] = useState(false);
  const [fundsAmount, setFundsAmount] = useState<number | null>(null);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [filteredAgencies, setFilteredAgencies] = useState<any[]>([]);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState<any | null>(null);

  // Utiliser useRef pour accéder aux valeurs actuelles sans causer de re-renders
  const agenciesRef = useRef(agencies);
  const tableFilterValuesRef = useRef(tableFilterValues);
  
  // Mettre à jour les refs quand les valeurs changent
  useEffect(() => {
    agenciesRef.current = agencies;
  }, [agencies]);
  
  useEffect(() => {
    tableFilterValuesRef.current = tableFilterValues;
  }, [tableFilterValues]);

  // Configuration de la recherche globale avec données pré-filtrées
  const lastCallRef = useRef<{ term: string; timestamp: number }>({ term: '', timestamp: 0 });
  const isProcessingRef = useRef(false);
  
  const handleGlobalSearch = useCallback((term, filtered) => {
    // Éviter les appels concurrents
    if (isProcessingRef.current) {
      return;
    }
    
    const now = Date.now();
    const { term: lastTerm, timestamp: lastTimestamp } = lastCallRef.current;
    
    // Éviter les appels identiques rapprochés (moins de 500ms maintenant)
    if (term === lastTerm && now - lastTimestamp < 500) {
      return;
    }
    
    isProcessingRef.current = true;
    lastCallRef.current = { term, timestamp: now };
    
    try {
      // Utiliser une approche plus simple et plus rapide
      if (term && term.trim()) {
        // Les données sont déjà filtrées par la recherche dans usePageSearch
        // Appliquer maintenant les filtres de table sur les résultats de recherche
        const finalFiltered = applyFilters(filtered || [], tableFilterValuesRef.current);
        setFilteredAgencies(finalFiltered);
      } else {
        // Si pas de recherche, utiliser les données filtrées par la table seulement
        const currentTableFiltered = applyFilters(agenciesRef.current, tableFilterValuesRef.current);
        setFilteredAgencies(currentTableFiltered);
      }
    } finally {
      // Libérer le verrou après un délai
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 100);
    }
  }, []); // Aucune dépendance pour stabiliser complètement le callback
  
  usePageSearch({
    data: agencies, // Utiliser les données brutes et laisser la recherche faire le filtrage
    searchFields: ['agencyName', 'licenceNumber', 'agencyEmail', 'phoneNum', 'managerFirstName', 'managerLastName', 'city', 'country.country'],
    onSearch: handleGlobalSearch
  });

  // Configuration des filtres pour TableFilter
  const filterConfig: FilterConfig[] = [
    {
      key: 'agencyName',
      type: 'text',
      label: t('admin.agencies.agencyName'),
      placeholder: t('filters.searchByName')
    },
    {
      key: 'agencyEmail',
      type: 'text',
      label: t('common.email'),
      placeholder: t('filters.searchByEmail')
    },
    {
      key: 'status',
      type: 'select',
      label: t('common.status'),
      options: [
        { label: t('status.active'), value: 'ACTIVE' },
        { label: t('admin.agencies.waitingActivation'), value: 'WAITING_ACTIVATION' },         
        { label: t('admin.agencies.available'), value: 'AVAILABLE' },
        { label: t('admin.agencies.loaded'), value: 'LOADED' },
        { label: t('admin.agencies.operatorBarred'), value: 'OPERATOR_BARRED' },
        { label: t('admin.agencies.preActivated'), value: 'PRE_ACTIVATED' },
        { label: t('admin.agencies.suspended'), value: 'SUSPENDED' },
        { label: t('admin.agencies.tariffHoliday'), value: 'TARIFF_HOLIDAY' },
        { label: t('admin.agencies.terminated'), value: 'TERMINATED' },
        { label: t('admin.agencies.testMode'), value: 'TEST_MODE' }
      ]
    },
  ];

  // Optimisation : calculer les données filtrées directement
  const finalFilteredAgencies = useMemo(() => {
    // Commencer avec les données brutes
    let result = agencies;
    
    // Appliquer d'abord les filtres de table
    if (Object.keys(tableFilterValues).length > 0) {
      result = applyFilters(result, tableFilterValues);
    }
    
    return result;
  }, [agencies, tableFilterValues]);

  // Initialisation des données filtrées optimisée
  useEffect(() => {
    setFilteredAgencies(finalFilteredAgencies);
  }, [finalFilteredAgencies]);

  useEffect(() => {
    fetchAgencies(pagination.current, pagination.pageSize, {
      ...filters,
    });
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchPendingAgencies = useCallback(
    async (page = 0, size = 10) => {
      try {
        const response = await agencyService.getAll({
          page,
          size,
          filters: { status: 'WAITING_ACTIVATION' }
        });
        setPendingCount(response.data?.totalElements || 0);
      } catch (error) {
        console.error('Erreur lors du chargement des agences en attente:', error);
        setPendingCount(0);
      }
    },
    []
  );

  const mapAgencyFiltersToBackend = (filters: any) => ({
    name: filters.name,
    email: filters.email,
    status: filters.status,
  });

  const fetchAgencies = useCallback(
    async (page = 0, size = pagination.pageSize, currentFilters: AgencyFilter = filters) => {
      try {
        setLoading(true);

        const response = await agencyService.getAll({
          page,
          size,
          filters: mapAgencyFiltersToBackend(currentFilters),
        });

        const pageData = response.data;

        setAgencies(pageData.content || []);
        setPagination({
          current: page,
          pageSize: size,
          total: pageData.totalElements || 0
        });
      } catch (error) {
        console.error(error);
        message.error(t('admin.agencies.loadError'));
        setAgencies([]);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.pageSize]
  );

  useEffect(() => {
    fetchAgencies(pagination.current, pagination.pageSize, filters);
  }, [pagination.current, pagination.pageSize, filters]);

  useEffect(() => {
    fetchPendingAgencies();
  }, [fetchPendingAgencies]);

  const handleTableChange = useCallback((paginationConfig) => {
    fetchAgencies(paginationConfig.current - 1, paginationConfig.pageSize, filters);
  }, [fetchAgencies, filters]);

  const handleFilterChange = useCallback((changedFilters: AgencyFilter) => {
    const newFilters = {
      ...filters,
      ...changedFilters
    };
    setFilters(newFilters);
  }, [filters, pagination.pageSize, fetchAgencies]);

  const showAddModal = () => {
    setIsEditMode(false);
    setCurrentAgency(null);
    form.resetFields();
    setModalVisible(true);
  };

  const showEditModal = (agency: any) => {
    setIsEditMode(true);
    setCurrentAgency(agency);
    form.setFieldsValue({
      agencyName: agency.agencyName,
      licenceNumber: agency.licenceNumber,
      agencyEmail: agency.agencyEmail,
      phone: agency.phoneNum, // Mappage de phoneNum vers phone
      address: agency.address,
      city: agency.city,
      country: agency.countryId, 
      postalCode: agency.zipCode, // Mappage de zipCode vers postalCode
      contactPerson: `${agency.managerFirstName || ''} ${agency.managerLastName || ''}`.trim()
    });
    setModalVisible(true);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    form.resetFields();
  };

  const splitContactPerson = (contactPerson = '') => {
    const parts = contactPerson.trim().split(/\s+/);

    return {
      managerFirstName: parts[0] || '',
      managerLastName: parts.slice(1).join(' ') || ''
    };
  };

  const mapFormToAgencyPayload = (values) => {
    const { managerFirstName, managerLastName } =
      splitContactPerson(values.contactPerson);

    return {
      agencyName: values.agencyName,
      licenceNumber: values.licenceNumber,
      address: values.address,
      zipCode: values.postalCode,          
      phoneNum: values.phone,              
      city: values.city,
      agencyEmail: values.agencyEmail,
      title: 'Mr',                         
      managerFirstName,
      managerLastName,
      language: 'fr',
      //countryId: 1001, //pour tester apres il faut voir
      countryId: values.country,
      currencyId: 1,
      password: values.password
    };
  };

  const handleModalSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();

      const payload = mapFormToAgencyPayload(values);

      if (isEditMode && currentAgency) {
        await agencyService.update(currentAgency.id, payload);
        message.success(t('admin.agencies.updateSuccess'));
      } else {
        await agencyService.create(payload);
        message.success(t('admin.agencies.createSuccess'));
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchAgencies(pagination.current, pagination.pageSize, filters);
    } catch (error) {
      message.error(t('admin.agencies.saveError'));
    }
  }, [form, isEditMode, currentAgency, pagination.current, pagination.pageSize, filters, fetchAgencies]);

  const handleDeleteAgency = useCallback(async (id: number) => {
    try {
      await agencyService.delete(id);
      message.success(t('admin.agencies.deleteSuccess'));
      fetchAgencies(pagination.current, pagination.pageSize, filters);
    } catch (error) {
      message.error(t('admin.agencies.deleteError'));
    }
  }, [pagination.current, pagination.pageSize, filters, fetchAgencies]);

  const handleStatusChange = useCallback(async (id: number, action: 'activate' | 'suspend' ) => {
    try {
      if (action === 'activate') {
        await agencyService.activate(id);
        message.success(t('admin.agencies.activateSuccess'));
      } else if (action === 'suspend') {
        await agencyService.suspend(id);
        message.success(t('admin.agencies.suspendSuccess'));
      } 
      
      fetchAgencies(pagination.current, pagination.pageSize, filters);
    } catch (error) {
      message.error(t('admin.agencies.saveError'));
    }
  }, [pagination.current, pagination.pageSize, filters, fetchAgencies]);

  const showAddFundsModal = (agency: Agency) => {
    setCurrentAgency(agency);
    setFundsAmount(null);
    setAddFundsModalVisible(true);
  };

  const showResetPasswordModal = (agency: any) => {
    setSelectedAgency(agency);
    setResetPasswordModalVisible(true);
  };

  const handleResetPasswordModalCancel = () => {
    setSelectedAgency(null);
    setResetPasswordModalVisible(false);
  };

  const handleAddFunds = useCallback(async () => {
    if (!currentAgency || !fundsAmount || fundsAmount <= 0) {
      message.error(t('admin.agencies.invalidAmount'));
      return;
    }
    
    try {
      await agencyService.addFunds(currentAgency.id, fundsAmount);
      message.success(t('admin.agencies.addFundsSuccess'));
      setAddFundsModalVisible(false);
      fetchAgencies(pagination.current, pagination.pageSize, filters);
    } catch (error) {
      message.error(t('admin.agencies.addFundsError'));
    }
  }, [currentAgency, fundsAmount, pagination.current, pagination.pageSize, filters, fetchAgencies]);

  const exportToExcel = async () => {
    try {
      const response = await agencyService.exportToExcel(filters);
      
      // Créer un lien pour télécharger le fichier
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `agencies_${moment().format('YYYY-MM-DD')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      message.success(t('admin.agencies.exportSuccess'));
    } catch (error) {
      message.error(t('admin.agencies.exportError'));
    }
  };

  const renderStatusTag = (status: string) => {
    const statusMap = {
      'ACTIVE': { color: 'green', text: t('agencyStatus.ACTIVE') },
      'PENDING': { color: 'blue', text: t('status.pending') },
      'SUSPENDED': { color: 'orange', text: t('agencyStatus.SUSPENDED') },
      'INACTIVE': { color: 'red', text: t('status.inactive') }
    };
    
    return (
      <Tag color={statusMap[status]?.color || 'default'}>
        {statusMap[status]?.text || status}
      </Tag>
    );
  };

  const renderSubscriptionTag = (status: string) => {
    const statusMap = {
      'ACTIVE': { color: 'green', text: t('agencyStatus.ACTIVE') },
      'TRIAL': { color: 'purple', text: t('status.trial') },
      'EXPIRED': { color: 'red', text: t('status.expired') },
      'CANCELLED': { color: 'orange', text: t('status.cancelled') }
    };
    
    return (
      <Tag color={statusMap[status]?.color || 'default'}>
        {statusMap[status]?.text || status}
      </Tag>
    );
  };
    const renderVerificationTag = (status: string) => {
      const statusMap: Record<string, { color: string; text: string }> = {
        ACTIVE: { color: 'green', text: t('agencyStatus.ACTIVE') },
        WAITING_ACTIVATION: { color: 'gold', text: t('agencyStatus.WAITING_ACTIVATION') },
        AVAILABLE: { color: 'blue', text: t('agencyStatus.AVAILABLE') },
        LOADED: { color: 'cyan', text: t('agencyStatus.LOADED') },
        OPERATOR_BARRED: { color: 'red', text: t('agencyStatus.OPERATOR_BARRED') },
        PRE_ACTIVATED: { color: 'geekblue', text: t('agencyStatus.PRE_ACTIVATED') },
        SUSPENDED: { color: 'volcano', text: t('agencyStatus.SUSPENDED') },
        TARIFF_HOLIDAY: { color: 'purple', text: t('agencyStatus.TARIFF_HOLIDAY') },
        TERMINATED: { color: 'default', text: t('agencyStatus.TERMINATED') },
        TEST_MODE: { color: 'magenta', text: t('agencyStatus.TEST_MODE') },
        INACTIVE: { color: 'orange', text: t('status.inactive') },
      };

  return (
    <Tag color={statusMap[status]?.color || 'default'}>
      {statusMap[status]?.text || status}
    </Tag>
  );
};

  // Gestionnaire pour approuver une agence
  const handleApproveAgency = useCallback(async (id: number) => {
    try {
      await agencyService.approveAgency(id);
      message.success(t('admin.agencies.activateSuccess'));
      fetchAgencies(pagination.current, pagination.pageSize, filters); // Rafraîchir la liste
      fetchPendingAgencies(); // Mettre à jour le compteur des agences en attente
    } catch (error) {
      message.error(t('admin.agencies.saveError'));
      console.error(error);
    }
  }, [pagination.current, pagination.pageSize, filters, fetchAgencies, fetchPendingAgencies]);
  
  // Gestionnaire pour rejeter une agence
  const handleRejectAgency = useCallback(async (id: number) => {
    try {
      await agencyService.rejectAgency(id);

      message.success(t('admin.agencies.agencyRejected'));

      // Rafraîchir la liste
      fetchAgencies(pagination.current, pagination.pageSize, filters);

      // Mettre à jour le compteur des agences en attente
      fetchPendingAgencies();

    } catch (error) {
      message.error(t('admin.agencies.rejectionError'));
      console.error(error);
    }
  }, [pagination.current, pagination.pageSize, filters, fetchAgencies, fetchPendingAgencies]);

  const renderActionMenu = useCallback((record: any) => (
    <Menu>
      <Menu.Item key="edit" onClick={() => showEditModal(record)}>
        <EditOutlined /> {t('admin.agencies.modify')}
      </Menu.Item>
      {record.status !== 'ACTIVE' && (
        <Menu.Item key="activate" onClick={() => handleStatusChange(record.id, 'activate')}>
          <CheckCircleOutlined /> {t('admin.agencies.activate')}
        </Menu.Item>
      )}
      {record.status === 'ACTIVE' && (
        <Menu.Item key="suspend" onClick={() => handleStatusChange(record.id, 'suspend')}>
          <PauseCircleOutlined /> {t('admin.agencies.suspend')}
        </Menu.Item>
      )}
      {record.status === 'WAITING_ACTIVATION' && (
        <>
          <Menu.Item key="approve" onClick={() => handleApproveAgency(record.id)}>
            <CheckCircleOutlined /> {t('admin.agencies.approve')}
          </Menu.Item>
          <Menu.Item key="reject" onClick={() => handleRejectAgency(record.id)}>
            <CloseCircleOutlined /> {t('admin.agencies.reject')}
          </Menu.Item>
        </>
      )}
      <Menu.Item key="addFunds" onClick={() => showAddFundsModal(record)}>
        <DollarOutlined /> {t('admin.agencies.addFunds')}
      </Menu.Item>
      {/*<Menu.Item key="resetPassword" onClick={() => showResetPasswordModal(record)}>
        <LockOutlined /> Réinitialiser le mot de passe
      </Menu.Item>*/}
      <Menu.Item key="delete" danger>
        <Popconfirm
          title={t('admin.agencies.confirmDelete')}
          onConfirm={() => handleDeleteAgency(record.id)}
          okText={t('common.yes')}
          cancelText={t('common.no')}
        >
          <DeleteOutlined /> {t('common.delete')}
        </Popconfirm>
      </Menu.Item>
    </Menu>
  ), [handleStatusChange, handleApproveAgency, handleRejectAgency, handleDeleteAgency, showEditModal, t]);
const debugTableData = useCallback(() => {
  console.log('Current agencies:', agencies);
  console.log('Is agencies array?', Array.isArray(agencies));
  console.log('Agencies length:', agencies?.length);
  console.log('First agency:', agencies?.[0]);
}, [agencies]);

  // Optimisation : mémoriser les fonctions de filtrage
  const createFilterDropdown = useCallback((placeholder: string) => ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }}>
      <Input
        placeholder={placeholder}
        value={selectedKeys[0]}
        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm()}
        style={{ marginBottom: 8, display: 'block' }}
      />
      <Space>
        <Button
          type="primary"
          onClick={() => confirm()}
          icon={<SearchOutlined />}
          size="small"
        >
          {t('common.search')}
        </Button>
        <Button onClick={() => clearFilters()} size="small">
          {t('common.reset')}
        </Button>
      </Space>
    </div>
  ), [t]);

  const columns = useMemo(() => [
    {
      title: t('common.number'),
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: t('common.name'),
      dataIndex: 'agencyName',
      key: 'agencyName',
      sorter: (a, b) => a.agencyName.localeCompare(b.agencyName),
      filterDropdown: createFilterDropdown(t('filters.searchByName')),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.agencyName.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: t('common.email'),
      dataIndex: 'agencyEmail',
      key: 'agencyEmail',
      sorter: (a, b) => a.agencyEmail.localeCompare(b.agencyEmail),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t('filters.searchByEmail')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.agencyEmail.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: t('common.phone'),
      dataIndex: 'phoneNum', // Utilisez phoneNum au lieu de phone
      key: 'phoneNum',
      sorter: (a, b) => a.phoneNum.localeCompare(b.phoneNum),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t('filters.searchByPhone')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.phoneNum.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: t('common.city'),
      dataIndex: 'city',
      key: 'city',
      sorter: (a, b) => a.city.localeCompare(b.city),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t('filters.searchByCity')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.city.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: t('common.address'),
      dataIndex: 'address',
      key: 'address',
      sorter: (a, b) => a.address.localeCompare(b.address),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t('filters.searchByAddress')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.address.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: renderVerificationTag, // Utilisez renderVerificationTag au lieu de renderStatusTag
      filters: [
        { text: t('agencyStatus.ACTIVE'), value: 'ACTIVE' },
        { text: t('agencyStatus.WAITING_ACTIVATION'), value: 'WAITING_ACTIVATION' },
        { text: t('agencyStatus.SUSPENDED'), value: 'SUSPENDED' },
        { text: t('status.inactive'), value: 'INACTIVE' },
        { text: t('agencyStatus.AVAILABLE'), value: 'AVAILABLE' },
        { text: t('agencyStatus.LOADED'), value: 'LOADED' },
        { text: t('agencyStatus.OPERATOR_BARRED'), value: 'OPERATOR_BARRED' },
        { text: t('agencyStatus.PRE_ACTIVATED'), value: 'PRE_ACTIVATED' },
        { text: t('agencyStatus.TARIFF_HOLIDAY'), value: 'TARIFF_HOLIDAY' },
        { text: t('agencyStatus.TERMINATED'), value: 'TERMINATED' },
        { text: t('agencyStatus.TEST_MODE'), value: 'TEST_MODE' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: t('common.balance'),
      dataIndex: 'balance',
      key: 'balance',
      render: (balance) => `${balance?.toFixed(2) || '0.00'} €`,
      sorter: (a, b) => (a.balance || 0) - (b.balance || 0),
    },
    {
      title: t('admin.agencies.lastModified'),
      dataIndex: 'lastModified',
      key: 'lastModified',
      render: (date) => date ? moment(date).format('DD/MM/YYYY HH:mm') : 'N/A',
      sorter: (a, b) => moment(a.lastModified).unix() - moment(b.lastModified).unix(),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => (
        <Dropdown overlay={renderActionMenu(record)} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ], [renderActionMenu, createFilterDropdown, t]);

  const [countryOptions, setCountryOptions] = useState([]);
  useEffect(() => {
    setLoading(true);
    getMergedCountryOptions(true)
      .then((options) => setCountryOptions(options))
      .finally(() => setLoading(false));
  }, []);
  
  return (
    <>      <Card 
        title={
          <>
            <Badge count={pagination.total}>
              <Button 
                  type="link" 
                  onClick={() => setFilters({})}
                >
              {t('admin.agencies.cardTitle')}</Button>
            </Badge>
            {pendingCount > 0 && (
              <Badge 
                count={pendingCount} 
                style={{ backgroundColor: '#faad14', marginLeft: 16 }}
                overflowCount={99}
              >
                <Button 
                  type="link" 
                  onClick={() => handleFilterChange({ status: 'WAITING_ACTIVATION' })}
                >
                  {t('admin.agencies.pendingAgencies')}
                </Button>
              </Badge>
            )}
          </>
        } 
        extra={
          <Space>
            {/*<Button 
              icon={<DownloadOutlined />} 
              onClick={exportToExcel}
            >
              Exporter
            </Button>*/}            
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={showAddModal}
            >
              {t('admin.agencies.addBtn')}
            </Button>
          </Space>
        }
      >
        <AgencyFilters
          filters={filters}
          setFilters={setFilters}
          onClearAll={() => {
            setFilters({});
          }}
        />
        <Table 
          columns={columns}
          scroll={{ x: 'max-content' }} // Permettre le défilement horizontal si nécessaire
          dataSource={filteredAgencies} // Utiliser les données filtrées
          loading={loading}
          rowKey={(record) => record.id || Math.random()} // Fallback pour la clé
          pagination={{
            ...pagination,
            current: pagination.current + 1, // Ant Design utilise une pagination 1-based
            showSizeChanger: true,
            showTotal: (total) => t('admin.agencies.totalAgencies', { total })
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: loading ? t('common.loading') : t('admin.agencies.noData')
          }}
        />
      </Card>

      {/* Modal pour ajouter/modifier une agence */}
      <Modal
        title={isEditMode ? t('admin.agencies.editModalTitle') : t('admin.agencies.addModalTitle')}
        visible={modalVisible}
        onCancel={handleModalCancel}
        onOk={handleModalSubmit}
        confirmLoading={loading}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="agencyName"
                label={t('admin.agencies.agencyName')}
                rules={[{ required: true, message: t('validation.nameRequired') }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="licenceNumber"
                label={t('admin.agencies.licenseNumber')}
                rules={[{ required: true, message: t('validation.licenseNumberRequired') }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="agencyEmail"
                label={t('common.email')}
                rules={[
                  { required: true, message: t('validation.emailRequired') ?? 'L\'email est requis' },
                  { type: 'email', message: 'Format d\'email invalide' }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label={t('common.phone')}
                rules={[{ required: true, message: t('admin.agencies.requiredPhone') ?? 'Le téléphone est requis' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          { !isEditMode && <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="password"
                label={t('common.password')}
                rules={[
                  { required: !isEditMode, message: t('validation.passwordRequired') },
                  { min: 8, message: t('validation.passwordMinLength') }
                ]}
              >
                <Input.Password />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="confirmPassword"
                label={t('common.confirmPassword')}
                dependencies={['password']}
                rules={[
                  { required: !isEditMode, message: t('agency.profile.confirmPassword')},
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(t('agency.profile.passwordMismatch'))
                      );
                    },
                  }),
                ]}
              >
                <Input.Password />
              </Form.Item>
            </Col>
          </Row>}

          <Form.Item
            name="address"
            label={t('common.address')}
            rules={[{ required: true, message: 'L\'adresse est requise' }]}
          >
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="city"
                label={t('common.city')}
                rules={[{ required: true, message: 'La ville est requise' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="country"
                label={t('common.country')}
                rules={[{ required: true, message: 'Le pays est requis' }]}
              >
                <Select showSearch optionFilterProp="children">
                  {countryOptions.map((country) => (
                    <Option key={country.id || country.value} value={country.id || country.value}>
                      {country.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="postalCode"
                label={t('common.postalCode')}
                rules={[{ required: true, message: 'Le code postal est requis' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="contactPerson"
            label={t('admin.agencies.contactPerson')}
            rules={[{ required: true, message: 'La personne de contact est requise' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal pour ajouter des fonds */}
      <Modal
        title={t('admin.agencies.addFundsTitle', { name: currentAgency?.agencyName || '' })}
        visible={addFundsModalVisible}
        onCancel={() => setAddFundsModalVisible(false)}
        onOk={handleAddFunds}
        confirmLoading={loading}
      >
        <Form layout="vertical">
          <Form.Item
            label={t('admin.agencies.addAmount')}
            rules={[{ required: true, message: t('admin.agencies.requiredAmount') }]}
          >
            <Input
              type="number"
              min={0}
              step={0.01}
              value={fundsAmount}
              onChange={(e) => setFundsAmount(parseFloat(e.target.value) || 0)}
              prefix="€"
              suffix="EUR"
            />
          </Form.Item>
          <p>{t('admin.agencies.currentBalance', { balance: currentAgency?.balance?.toFixed(2) || 0 })}</p>
        </Form>
      </Modal>

      {/* Modal pour réinitialiser le mot de passe */}
      <AdminResetPasswordModal
        visible={resetPasswordModalVisible}
        onCancel={handleResetPasswordModalCancel}
        userId={selectedAgency?.id}
        userEmail={selectedAgency?.agencyEmail}
        userType="agency"
      />

      {process.env.NODE_ENV === 'development' && (
  <div style={{ margin: '16px 0', padding: '8px', backgroundColor: '#f0f0f0' }}>
    <p>Debug Info:</p>
    <p>Agencies length: {agencies?.length || 0}</p>
    <p>Is array: {Array.isArray(agencies).toString()}</p>
    <p>Loading: {loading.toString()}</p>
    <button onClick={debugTableData}>Debug Data</button>
  </div>
)}
    </>
  );
};

export default AgenciesList;